import { ICommandBarItemProps } from "@fluentui/react";

import { ITerm } from "../interfaces/ITerm.interface";

import { checkUserPermission } from "./checkUserPermissions";

import {
  PermissionsAction,
  PermissionsConfig,
  PermissionsPosModule,
  PermissionsScope
} from "@/common/types/permissions.types";
import { ApprovalStep } from "@/components/Contracts/contract.interface";
import { ContractStatus } from "@/modules/Contracts/enums";

interface BuildCommandBarItemsProps {
  term: ITerm;
  downloadTerm: (data?: { signed?: boolean }) => Promise<void>;
  permissions: PermissionsConfig;
  advanceStep: () => Promise<void>;
  cancelOrRejectTerm: (actionName: string) => Promise<void>;
  handleDisplayTermLogs: (isOpen: boolean) => void;
  isProcessing: boolean;
}

const terminalTermSteps: Array<ContractStatus> = [
  ContractStatus.CANCELED,
  ContractStatus.REJECTED,
  ContractStatus.DONE,
  ContractStatus.TIMED_OUT
] as const;

export const buildCommandBarItems = ({
  term,
  downloadTerm,
  permissions,
  advanceStep,
  cancelOrRejectTerm,
  handleDisplayTermLogs,
  isProcessing
}: BuildCommandBarItemsProps) => {
  const termsPermissions =
    permissions[PermissionsScope.POS]?.[PermissionsPosModule.TERMS];

  const changeStatusOptions = [];
  const downloadOptions = [
    {
      key: "Imprimir contrato original",
      text: "Documento Original",
      onClick: () => void downloadTerm(),
      id: `original`
    },
    {
      key: "Imprimir contrato assinado",
      text: "Documento Assinado",
      onClick: () => void downloadTerm({ signed: true }),
      id: `signed`
    }
  ];

  const currentStep = term?.currentStep;
  const isTerminalStep = terminalTermSteps.includes(currentStep?.internalName);

  const rejectedAction = term?.approvalFlow?.steps.find(
    step => step.internalName === ContractStatus.REJECTED
  );
  const canceledAction = term?.approvalFlow?.steps.find(
    step => step.internalName === ContractStatus.CANCELED
  );

  const userHasPermission = checkUserPermission({
    permissions,
    scope: PermissionsScope.POS,
    module: PermissionsPosModule.TERMS,
    action: term?.nextStep?.interactionAction
  });

  const canAdvanceStep =
    !isTerminalStep &&
    currentStep?.interactionType === "ACTION_STATUS" &&
    userHasPermission;

  const canPerformTermAction = (action?: ApprovalStep): boolean =>
    Boolean(
      !isTerminalStep &&
      action &&
      term?.nextStep &&
      termsPermissions?.some(
        permission =>
          permission === PermissionsAction.ALL ||
          permission === action.interactionAction
      )
    );

  const canCancelTerm = canPerformTermAction(canceledAction);
  const canRejectTerm = canPerformTermAction(rejectedAction);

  if (canAdvanceStep) {
    changeStatusOptions.push({
      key: currentStep.id,
      text: currentStep.interactionName,
      onClick: () => advanceStep(),
      id: `currentStep-${currentStep?.internalName}-button`.replace(/\s/g, "")
    });
  }

  if (canCancelTerm) {
    changeStatusOptions.push({
      key: canceledAction?.id,
      text: canceledAction?.interactionName,
      onClick: () => cancelOrRejectTerm("cancel"),
      id: `nextStep-${canceledAction?.internalName}-button`.replace(/\s/g, "-")
    });
  }

  if (canRejectTerm) {
    changeStatusOptions.push({
      key: rejectedAction?.id,
      text: rejectedAction?.interactionName,
      onClick: () => cancelOrRejectTerm("reject"),
      id: `nextStep-${rejectedAction?.internalName}-button`.replace(/\s/g, "-")
    });
  }

  const isDone = currentStep?.internalName === ContractStatus.DONE;
  const isPrintDisabled = !term?.pdfPath;
  const isChangeStatusDisabled =
    isProcessing || isTerminalStep || changeStatusOptions.length === 0;

  const items: ICommandBarItemProps[] = [
    {
      id: "button-print",
      key: "Imprimir",
      text: "Imprimir",
      iconProps: { iconName: "Print" },
      subMenuProps: isDone ? { items: downloadOptions } : undefined,
      onClick: isDone ? undefined : () => void downloadTerm(),
      disabled: isPrintDisabled
    },
    {
      id: "button-change-status",
      key: "status",
      text: "Alterar status",
      iconProps: { iconName: "Sync" },
      subMenuProps: { items: changeStatusOptions },
      disabled: isChangeStatusDisabled
    }
  ];

  const canReadTermsLogs = permissions[PermissionsScope.POS]?.[
    PermissionsPosModule.TERMS
  ]?.some(
    action =>
      action === PermissionsAction.ALL ||
      action === PermissionsAction.TERM_OCCURRENCES
  );

  if (canReadTermsLogs) {
    items.push({
      id: "button-contract-history",
      key: "termOccurrences",
      text: "Histórico",
      iconProps: {
        iconName: "FullHistory"
      },
      onClick: () => handleDisplayTermLogs(true)
    });
  }

  return items;
};
